import styled from '@emotion/styled';
import { Link, ProgressBar, tokens, Tooltip } from '@hotjar/hotsauce';
import { roundNumber } from '@hotjar/insights-common/lib/number';
import { EmotionIcon, useEmotionLabels } from '@hotjar/webapp/features/Feedback/EmotionIcon';
import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import type { EmotionScore } from '@hotjar/insights-common/constants/emotions';
import { EmotionScores } from '@hotjar/insights-common/constants/emotions';
import { RATING_TYPE } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/RatingFilter/RatingFilterType';
import { formatPercentage, useTranslation, Trans } from '@hotjar/translations';

const Wrapper = styled.div`
  background-color: ${tokens.color.neutral_100};
  display: grid;
  grid-template-columns: 275px auto;
  margin: 0 30px 30px;
`;

const Average = styled.div`
  padding: ${tokens.spacer.x4} ${tokens.spacer.x8};
  border-right: 1px dashed ${tokens.color.ui_border_light};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AverageEmotion = styled.div`
  margin-bottom: ${tokens.spacer.x2};
`;

const AverageText = styled.div`
  ${tokens.text('header5')}
  display: inline-block;
  font-weight: normal;
  margin-bottom: 0;
`;

const Ratings = styled.div`
  padding: ${tokens.spacer.x2} ${tokens.spacer.x4};
  display: flex;
  flex-flow: row wrap;
  align-items: center;
`;

const Rating = styled.button<{ emotionStyle: EmotionStyle }>`
  ${tokens.text('caption')};
  ${tokens.radius.l};
  padding: ${tokens.spacer.x2};
  margin-right: ${tokens.spacer.x1};
  border: none;
  background: none;
  min-width: ${(p) => (p.emotionStyle === 'star' ? 200 : 170)}px;
  border-radius: ${tokens.radius.m};

  &:hover {
    background-color: ${tokens.color.ui_background};
  }
`;

const RatingDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RatingText = styled.div`
  margin-left: ${tokens.spacer.x1};
`;

// TODO: Remove this workaround when DS allows to configure progress color (DSC-780)
const RatingProgressBar = styled(ProgressBar)`
  margin-top: ${tokens.spacer.x1};
  > div {
    background-color: ${tokens.color.azure};
  }
`;

export const RATING_TYPES: RATING_TYPE[] = [
  RATING_TYPE.HATE,
  RATING_TYPE.DISLIKE,
  RATING_TYPE.NEUTRAL,
  RATING_TYPE.LIKE,
  RATING_TYPE.LOVE,
];

export interface RatingSummaryProps {
  emotionStyle: EmotionStyle;
  averageSentiment: number;
  responsesCount: number;
  sentimentCount: { [key in EmotionScore]: number };
  onRatingClicked: (score: RATING_TYPE) => void;
  onResponsesClicked: () => void;
}

export function RatingSummary(props: RatingSummaryProps): JSX.Element {
  const {
    emotionStyle,
    averageSentiment,
    responsesCount,
    sentimentCount,
    onRatingClicked,
    onResponsesClicked,
  } = props;

  const { t, i18n } = useTranslation('feedback');

  const emotionLabels = useEmotionLabels();

  return (
    <Wrapper>
      <Average>
        <AverageEmotion>
          <EmotionIcon
            emotionStyle={emotionStyle}
            score={roundNumber(averageSentiment - 1) as EmotionScore}
            size={emotionStyle === 'star' ? 'm' : 'l'}
          />
        </AverageEmotion>
        <AverageText>
          <Trans i18nKey="feedback:incoming-feedback.summary.score-out-of-5">
            <strong>{{ value: roundNumber(averageSentiment, 1) }}</strong>
          </Trans>
        </AverageText>
        <Link kind="inline" as="button" onClick={onResponsesClicked}>
          {t('count-responses', { count: responsesCount })}
        </Link>
      </Average>
      <Ratings>
        {EmotionScores.map((score) => {
          const percentage = roundNumber((sentimentCount[score] * 100) / responsesCount, 2);

          return (
            <Rating
              key={score}
              onClick={() => onRatingClicked(RATING_TYPES[score])}
              emotionStyle={emotionStyle}
            >
              <RatingDetails>
                <Tooltip text={emotionLabels[score]} isDisabled={emotionStyle === 'star'}>
                  <EmotionIcon
                    emotionStyle={emotionStyle}
                    score={score}
                    size={emotionStyle === 'star' ? 's' : 'm'}
                  />
                </Tooltip>
                <RatingText>
                  <strong>{sentimentCount[score]}</strong>&nbsp;
                  <span>({formatPercentage(percentage, i18n)})</span>
                </RatingText>
              </RatingDetails>
              <RatingProgressBar currentValue={percentage} />
            </Rating>
          );
        })}
      </Ratings>
    </Wrapper>
  );
}
